/**
 * AdminSystem — System status & one-click GitHub sync.
 *
 * Sections:
 *  • Git status card     — current/remote SHA, behind count, "in sync" pill
 *  • One-click sync btn  — super_admin only; writes /opt/lawos/.force-sync
 *  • Pending status      — polls every 5 sec when sync is pending
 *  • Auto-update log     — tail of /var/log/lawos-autoupdate.log
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
    CheckCircle2, GitBranch, GitCompare, Loader2, RefreshCw,
    ShieldAlert, Zap,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Ui";

const fmtRelTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata", year: "numeric", month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
        }) + " IST";
    } catch { return iso; }
};

export default function AdminSystem() {
    const [user, setUser] = useState(null);
    const [status, setStatus] = useState(null);
    const [logText, setLogText] = useState("");
    const [loading, setLoading] = useState(false);
    const [triggering, setTriggering] = useState(false);

    useEffect(() => {
        api.get("/auth/me").then(r => setUser(r.data)).catch(() => {});
    }, []);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [s, l] = await Promise.all([
                api.get("/admin/system/git-status"),
                api.get("/admin/system/autoupdate-log", { params: { lines: 80 } }),
            ]);
            setStatus(s.data);
            setLogText(l.data.content || "");
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    // Auto-poll every 5 sec while a sync is pending
    useEffect(() => {
        if (!status?.sync_pending && status?.in_sync) return;
        if (!status?.sync_pending) return;
        const t = setInterval(loadAll, 5000);
        return () => clearInterval(t);
    }, [status?.sync_pending, status?.in_sync, loadAll]);

    const triggerSync = async () => {
        if (user?.role !== "super_admin") {
            toast.error("Only super_admin can trigger a sync");
            return;
        }
        if (!window.confirm(
            "Trigger a one-click sync from GitHub to VPS?\n\n" +
            "• Latest code will be pulled within ~60 seconds\n" +
            "• Backend + frontend will rebuild (rolling restart)\n" +
            "• Brief ~30 second touch in connectivity expected\n\n" +
            "Proceed?"
        )) return;
        setTriggering(true);
        try {
            const { data } = await api.post("/admin/system/sync");
            if (data.ok) {
                toast.success(data.message || "Sync requested");
                await loadAll();
            } else {
                toast.error(data.error || "Sync request failed");
            }
        } catch (e) { toast.error(apiError(e)); }
        finally { setTriggering(false); }
    };

    const cancelSync = async () => {
        try {
            const { data } = await api.delete("/admin/system/sync");
            toast.success(data.message || "Pending sync cancelled");
            await loadAll();
        } catch (e) { toast.error(apiError(e)); }
    };

    const isSuper = user?.role === "super_admin";

    // Sync state derivation
    let pill, pillCls;
    if (!status?.ok) {
        pill = "Status unavailable"; pillCls = "bg-slate-100 text-slate-700 border-slate-300";
    } else if (status.sync_pending) {
        pill = "Sync in progress…"; pillCls = "bg-amber-100 text-amber-800 border-amber-300";
    } else if (status.in_sync) {
        pill = "In sync ✓"; pillCls = "bg-emerald-100 text-emerald-800 border-emerald-300";
    } else {
        pill = `${status.behind_count} commit${status.behind_count === 1 ? "" : "s"} behind`;
        pillCls = "bg-rose-100 text-rose-800 border-rose-300";
    }

    return (
        <div className="space-y-6" data-testid="admin-system-page">
            <PageHeader
                title="System & GitHub Sync"
                subtitle="VPS deployment health · one-click code sync from GitHub"
            />

            {/* ===== Git status card ===== */}
            <div className="border border-slate-200 rounded-lg bg-white p-5" data-testid="git-status-card">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[280px]">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                            <GitBranch size={16} />
                            <span>Branch</span>
                            <code className="text-slate-900 font-mono">{status?.branch || "—"}</code>
                            <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${pillCls}`} data-testid="sync-pill">
                                {pill}
                            </span>
                        </div>

                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="text-xs">
                                <div className="text-slate-500 uppercase tracking-wide">Current (on VPS)</div>
                                <div className="font-mono text-slate-900 mt-0.5">{status?.local_sha || "—"}</div>
                                <div className="text-slate-600 mt-0.5 truncate" title={status?.local_message}>{status?.local_message || "—"}</div>
                            </div>
                            <div className="text-xs">
                                <div className="text-slate-500 uppercase tracking-wide">Latest (on GitHub)</div>
                                <div className="font-mono text-slate-900 mt-0.5">{status?.remote_sha || "—"}</div>
                                <div className="text-slate-600 mt-0.5 truncate" title={status?.remote_message}>{status?.remote_message || "—"}</div>
                            </div>
                        </div>

                        {status?.fetch_error && (
                            <div className="mt-3 p-2 rounded bg-amber-50 border border-amber-300 text-xs text-amber-900">
                                ⚠ git fetch warning: {status.fetch_error}
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col items-end gap-2">
                        {!isSuper && (
                            <span className="text-xs text-slate-500 italic">Only super_admin can trigger sync</span>
                        )}
                        {status?.sync_pending ? (
                            <button
                                onClick={cancelSync}
                                disabled={!isSuper}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                                data-testid="cancel-sync-btn"
                            >
                                <ShieldAlert size={16} /> Cancel pending sync
                            </button>
                        ) : (
                            <button
                                onClick={triggerSync}
                                disabled={!isSuper || triggering || status?.in_sync}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                                    status?.in_sync
                                        ? "bg-slate-400"
                                        : "bg-rose-600 hover:bg-rose-700"
                                }`}
                                title={status?.in_sync ? "Already in sync — nothing to deploy" : "Pull latest from GitHub and rebuild now"}
                                data-testid="trigger-sync-btn"
                            >
                                {triggering ? <Loader2 className="animate-spin" size={16} /> : <Zap size={16} />}
                                {status?.in_sync ? "Already in sync" : "Sync from GitHub now"}
                            </button>
                        )}
                        <button
                            onClick={loadAll}
                            className="inline-flex items-center gap-1 px-3 py-1 rounded text-xs border border-slate-300 hover:bg-slate-50"
                            data-testid="refresh-status-btn"
                        >
                            <RefreshCw size={12} /> Refresh
                        </button>
                    </div>
                </div>

                {status?.sync_pending && (
                    <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-300 flex items-start gap-2" data-testid="sync-pending-banner">
                        <Loader2 className="animate-spin text-amber-700 mt-0.5 flex-shrink-0" size={16} />
                        <div className="text-sm text-amber-900">
                            <strong>Sync queued.</strong> The host auto-update cron will pick this up within 60 seconds.
                            Backend + frontend containers will rebuild and rolling-restart (~30s of brief touch).
                            This page auto-refreshes every 5 seconds — when sync completes, the pill above will turn green.
                        </div>
                    </div>
                )}
            </div>

            {/* ===== How it works ===== */}
            <div className="border border-slate-200 rounded-lg bg-slate-50 p-4 text-sm">
                <div className="flex items-center gap-2 font-medium text-slate-900 mb-2">
                    <GitCompare size={16} /> How one-click sync works
                </div>
                <ol className="space-y-1 ml-6 list-decimal text-slate-700 text-xs">
                    <li>You click <strong>Sync from GitHub now</strong> → backend writes <code>/opt/lawos/.force-sync</code></li>
                    <li>Host cron runs <code>auto-update.sh</code> every 60 seconds, sees the trigger, and pulls + rebuilds</li>
                    <li>Backend + frontend Docker images are rebuilt (typically ~30–60 seconds)</li>
                    <li>Containers are rolling-restarted (<code>docker compose up -d --no-deps</code>) — zero/minimal downtime</li>
                    <li>This page auto-refreshes and shows the new commit SHA matching GitHub</li>
                </ol>
            </div>

            {/* ===== Settings snapshot (export / import) ===== */}
            <div className="border border-slate-200 rounded-lg bg-white p-5 space-y-3" data-testid="settings-snapshot-card">
                <div className="flex items-center gap-2 font-medium text-slate-900">
                    <GitCompare size={16}/> Settings snapshot — safety net
                </div>
                <div className="text-xs text-slate-600">
                    Export ALL admin-customizable settings (plans, addons, branding, email
                    template, GST, chatbot prompt, master data, notification providers) as
                    a single JSON file. Re-upload it any time to roll back. Tenant business
                    data (cases, clients, invoices) is NOT included — use the MongoDB backup
                    for that.
                </div>
                <div className="flex flex-wrap gap-2">
                    <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings-snapshot/export`}
                        data-testid="settings-export-btn"
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90"
                    >Download snapshot</a>
                    <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 text-xs cursor-pointer hover:bg-slate-50" data-testid="settings-import-label">
                        Upload snapshot to restore
                        <input
                            type="file"
                            accept="application/json"
                            data-testid="settings-import-input"
                            className="hidden"
                            onChange={async (e) => {
                                const f = e.target.files?.[0];
                                if (!f) return;
                                if (!window.confirm(`Restore settings from ${f.name}? Singleton config docs will be REPLACED; plans/addons/master-data will be merged by code. Existing rows not in the snapshot are kept untouched.`)) {
                                    e.target.value = "";
                                    return;
                                }
                                try {
                                    const fd = new FormData();
                                    fd.append("file", f);
                                    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings-snapshot/import`, {
                                        method: "POST",
                                        credentials: "include",
                                        body: fd,
                                    });
                                    const data = await r.json();
                                    if (!r.ok) throw new Error(data?.detail || r.statusText);
                                    alert(`Snapshot restored.\n\n${JSON.stringify(data.summary, null, 2)}`);
                                } catch (err) {
                                    alert(`Restore failed: ${err.message || err}`);
                                } finally {
                                    e.target.value = "";
                                }
                            }}
                        />
                    </label>
                </div>
            </div>

            {/* ===== Recent auto-update log ===== */}
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-slate-900">Last 80 lines of <code>/var/log/lawos-autoupdate.log</code></div>
                    <button onClick={loadAll} className="text-xs underline hover:no-underline">
                        <RefreshCw size={11} className="inline mr-1" /> reload
                    </button>
                </div>
                <pre className="bg-slate-900 text-slate-100 text-xs p-3 rounded-lg overflow-auto max-h-[400px]" data-testid="autoupdate-log">
                    {loading ? "Loading..." : (logText || "(no log entries yet)")}
                </pre>
            </div>
        </div>
    );
}
